/**
 * useLockManagement Hook
 * 
 * Manages transcript lock state and operations
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import LockService from '../../../services/transcript/LockService';

/**
 * Hook for managing transcript lock
 * @param {string} sessionId - Session ID
 * @returns {Object} Lock state and operations
 */
const useLockManagement = (sessionId) => {
  const [lockStatus, setLockStatus] = useState({
    locked: false,
    locked_by: null,
    locked_by_name: null,
    locked_at: null,
    has_lock: false,
  });
  const [hasLock, setHasLock] = useState(false);
  const [lockLoading, setLockLoading] = useState(false);
  const [lockInitializing, setLockInitializing] = useState(true);
  const lockCheckIntervalRef = useRef(null);

  /**
   * Check lock status
   */
  const checkLockStatus = useCallback(async () => {
    if (!sessionId) return;

    try {
      const response = await LockService.getLockStatus(sessionId);
      if (response.success) {
        setLockStatus(response.lockInfo);
        setHasLock(response.lockInfo.has_lock);
      }
    } catch (error) {
      // Error handling is done in LockService
    }
  }, [sessionId]);

  /**
   * Acquire lock
   */
  const acquireLock = useCallback(async () => {
    if (!sessionId) return;

    setLockLoading(true);
    try {
      const response = await LockService.acquireLock(sessionId);
      if (response.success) {
        await checkLockStatus();
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      throw error;
    } finally {
      setLockLoading(false);
    }
  }, [sessionId, checkLockStatus]);

  /**
   * Release lock
   */
  const releaseLock = useCallback(async () => {
    if (!sessionId) return;

    setLockLoading(true);
    try {
      const response = await LockService.releaseLock(sessionId);
      if (response.success) {
        await checkLockStatus();
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      throw error;
    } finally {
      setLockLoading(false);
    }
  }, [sessionId, checkLockStatus]);

  /**
   * Force override lock
   */
  const forceOverrideLock = useCallback(async () => {
    if (!sessionId) return;

    setLockLoading(true);
    try {
      const response = await LockService.forceOverrideLock(sessionId);
      if (response.success) {
        await checkLockStatus();
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      throw error;
    } finally {
      setLockLoading(false);
    }
  }, [sessionId, checkLockStatus]);

  /**
   * Initialize lock checking on mount
   */
  useEffect(() => {
    if (!sessionId) {
      setLockInitializing(false);
      return;
    }

    checkLockStatus().finally(() => setLockInitializing(false));

    // Poll lock status at regular intervals
    lockCheckIntervalRef.current = setInterval(() => {
      checkLockStatus();
    }, LockService.getLockCheckInterval());

    // Cleanup on unmount
    return () => {
      if (lockCheckIntervalRef.current) {
        clearInterval(lockCheckIntervalRef.current);
      }
    };
  }, [sessionId, checkLockStatus]);

  return {
    lockStatus,
    hasLock,
    lockLoading,
    lockInitializing,
    checkLockStatus,
    acquireLock,
    releaseLock,
    forceOverrideLock,
  };
};

export default useLockManagement;

